import { Suspense, lazy } from 'react';
import { Navigate, useRoutes, useLocation, Outlet } from 'react-router-dom';
// layouts
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
import BlankLayout from '../layouts/BlankLayout';
// guards
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
// import RoleBasedGuard from '../guards/RoleBasedGuard';
// config
import { defaultLang, PATH_AFTER_LOGIN } from '../config';
// components
import LoadingScreen from '../components/LoadingScreen';
import LanguageRouter from '../layouts/LanguageRouter';

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) =>
  (
    <Suspense fallback={<LoadingScreen loadable={true} />}>
      <Component {...props} />
    </Suspense>
  );

export default function Router() {
  return useRoutes([
    process.env.REACT_APP_IS_MAINTENANCE === 'true'
      ? { path: 'maintenance', element: <Maintenance /> }
      : {
          path: '/',
          element: <Navigate to={`/${defaultLang?.value}`} replace />,
        },
    {
      path: ':lang',
      element: <LanguageRouter />,
      children: [
        {
          path: 'auth',
          children: [
            {
              path: 'login',
              element: <DashboardLayout />,
              children: [
                {
                  element: <Home popupAuth={'1'} />,
                  index: true,
                },
              ],
            },
            {
              path: 'register',
              element: <DashboardLayout />,
              children: [
                {
                  element: (
                    <GuestGuard>
                      <Register />
                    </GuestGuard>
                  ),
                  index: true,
                },
              ],
            },
            { path: 'register-unprotected', element: <Register /> },
            { path: 'reset-password/:token', element: <ResetPassword /> },
            { path: 'new-password', element: <NewPassword /> },
            { path: 'verify', element: <VerifyCode /> },
          ],
        },

        // Dashboard Routes
        {
          element: (
            // <AuthGuard>
            <DashboardLayout />
            // </AuthGuard>
          ),
          children: [
            { element: <Home />, index: true },
            { path: 'home', element: <Navigate to={`/${defaultLang?.value}${PATH_AFTER_LOGIN}`} replace /> },
            { path: 'games/:categSlug', element: <GamesByCateg /> },
            { path: 'games/:categSlug/:companyId', element: <GamesByCateg /> },
            { path: 'games/provider/:categSlug/:companyId', element: <GamesByCateg /> },

            { path: 'promotion', element: <Promotion /> },
            { path: 'betting_pass', element: <BettingPass /> },
            // { path: 'download', element: <DownloadApk /> },

            // Auth Routes
            {
              element: (
                <AuthGuard>
                  <Outlet />
                </AuthGuard>
              ),
              children: [
                { element: <Navigate to={`${defaultLang?.value}${PATH_AFTER_LOGIN}`} replace />, index: true },
                { path: 'history/bet', element: <GameHistory /> },
                { path: 'history/transaction', element: <TransactionHistory /> },

                { path: 'referral', element: <Referral /> },

                { path: 'vip', element: <VipTable /> },
                { path: 'agent', element: <Agent /> },

                { path: 'invite', element: <Invite /> },

                // mini game routes
                { path: 'spin', element: <SpinTheWheel /> },

                { path: 'promotion/voucher', element: <Voucher /> },

                // User Routes
                {
                  path: 'user',
                  children: [
                    { path: 'setting', element: <Setting /> },
                    { path: 'account', element: <AccountProfile /> },
                    { path: 'banks', element: <MemberBankList /> },
                    { path: 'wallet', element: <Wallet /> },
                    { path: 'change-password', element: <ChangePassword /> },
                  ],
                },
              ],
            },
          ],
        },

        {
          path: 'payStatus',
          element: <LogoOnlyLayout />,
          children: [
            {
              path: ':paymentMethod',
              element: <PaymentStatus />,
              index: true,
            },
          ],
        },

        // Download Page
        {
          path: 'download',
          element: <DownloadPage />,
        },
      ],
    },

    // Main Routes
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: 'coming-soon', element: <ComingSoon /> },
        { path: 'maintenance', element: <Maintenance /> },
        { path: '500', element: <Page500 /> },
        { path: '404', element: process.env.REACT_APP_IS_MAINTENANCE === 'true' ? <Maintenance /> : <Page404 /> },
        { path: '403', element: <Page403 /> },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },
    // {
    //   path: '/',
    //   element: <MainLayout />,
    //   children: [
    //     { element: <HomePage />, index: true },
    //     { path: 'about-us', element: <About /> },
    //     { path: 'contact-us', element: <Contact /> },
    //     { path: 'faqs', element: <Faqs /> },
    //   ],
    // },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}

// AUTHENTICATION
const Register = Loadable(lazy(() => import('../pages/auth/Register')));
const ResetPassword = Loadable(lazy(() => import('../pages/auth/ResetPassword')));
const NewPassword = Loadable(lazy(() => import('../pages/auth/NewPassword')));
const VerifyCode = Loadable(lazy(() => import('../pages/auth/VerifyCode')));
const ChangePassword = Loadable(lazy(() => import('../pages/auth/ChangePassword')));

// HOME
const Home = Loadable(lazy(() => import('../views/game/Home')));

// // DOWNLOAD
// const DownloadApk = Loadable(lazy(() => import('../views/game/DownloadApk')));

// GAMES
const GamesByCateg = Loadable(lazy(() => import('../views/game/GamesByCateg')));

// GAME HISTORY
const GameHistory = Loadable(lazy(() => import('../views/history/GameHistory')));

// TRANSACTION HISTORY
const TransactionHistory = Loadable(lazy(() => import('../views/history/TransactionHistory')));

// USER
const Setting = Loadable(lazy(() => import('../views/user/Setting')));
const AccountProfile = Loadable(lazy(() => import('../views/user/AccountProfile')));
const MemberBankList = Loadable(lazy(() => import('../views/user/MemberBankList')));
const Wallet = Loadable(lazy(() => import('../views/user/Wallet')));

// INVITE
const Invite = Loadable(lazy(() => import('../views/share/Invite')));

// PROMOTION
const Promotion = Loadable(lazy(() => import('../views/promotion/Promotion')));
const ViewPromotion = Loadable(lazy(() => import('../views/promotion/ViewPromotion')));
const AngPau = Loadable(lazy(() => import('../views/promotion/AngPau')));
const BettingPass = Loadable(lazy(() => import('../views/promotion/BettingPass')));
const SpinTheWheel = Loadable(lazy(() => import('../views/promotion/SpinTheWheel')));
const Voucher = Loadable(lazy(() => import('../views/promotion/Voucher')));

// PAYMENT STATS
const PaymentStatus = Loadable(lazy(() => import('../pages/PaymentStatus')));

// REFERRAL
const Referral = Loadable(lazy(() => import('../views/referral/Referral')));

// Bonus
const Vip = Loadable(lazy(() => import('../views/referral/Vip')));
const VipTable = Loadable(lazy(() => import('../views/referral/VipTable')));
const Agent = Loadable(lazy(() => import('../views/referral/Agent')));

const DownloadPage = Loadable(lazy(() => import('../views/DownloadPage')));

// MAIN
const HomePage = Loadable(lazy(() => import('../pages/Home')));
const ComingSoon = Loadable(lazy(() => import('../pages/ComingSoon')));
const Maintenance = Loadable(lazy(() => import('../pages/Maintenance')));
const Page500 = Loadable(lazy(() => import('../pages/Page500')));
const Page403 = Loadable(lazy(() => import('../pages/Page403')));
const Page404 = Loadable(lazy(() => import('../pages/Page404')));
